import { useContext } from 'react';

import useCrossProductUrlWrapper from '@atlassiansox/analytics-cross-product-interaction-client/use-cross-product-url-wrapper';
import { di } from 'react-magnetic-di';

import { SmartCardContext } from '@atlaskit/link-provider/context';
import { fg } from '@atlaskit/platform-feature-flags/fg';

/** XPC navigation surface identifier (capability-surface, e.g. rovoAgents-agentsList). */
export type CrossProductBridge = string;

export type AgentCrossProductUrlOptions = {
	xpcBridge: CrossProductBridge;
};

const identity = (url: string): string => url;

/**
 * Host-aware XPC URL wrapper for shared AI Mate agent components.
 *
 * These components render across multiple host products (Studio, and Rovo Chat
 * embedded in Jira/Confluence/Townsquare/...). The XPC `source` reflects the
 * actual host, read from `SmartCardProvider`'s `xpcProduct`. When no host
 * product is available the wrapper is an identity no-op, so navigations are
 * never mis-attributed. Gated by `ai-mate-xpc` as a host-neutral kill-switch.
 *
 * Product-agnostic: depends only on the platform XPC primitive, not on any
 * single product package.
 */
export const useAgentCrossProductUrlWrapper = ({
	xpcBridge,
}: AgentCrossProductUrlOptions): ((url: string) => string) => {
	di(useCrossProductUrlWrapper);

	// Read defensively: `useSmartLinkContext()` throws without a provider, but
	// these components can render outside one.
	const cardContext = useContext(SmartCardContext);
	const xpcProduct = cardContext?.xpcProduct;

	// Always call the hook (Rules of Hooks); result unused when we return identity.
	const wrapUrl = useCrossProductUrlWrapper({
		bridge: xpcBridge,
		product: xpcProduct ?? '',
	});

	// No host product (unknown host / no provider) or kill-switch off: do not report.
	if (!xpcProduct || !fg('ai-mate-xpc')) {
		return identity;
	}

	return wrapUrl;
};
